<script setup lang="ts">
import { GridStack } from 'gridstack';
import 'gridstack/dist/gridstack.css';
import { Assert } from 'shared/assert';
import { onMounted, useTemplateRef } from 'vue';
import type { DashboardComponent } from '../domain/dashboard';
import DashboardComponentItem from './DashboardComponentItem.vue';

const dashboardGridRef = useTemplateRef('dashboard-grid');
let gridStack: null | GridStack = null;

const components = defineModel<DashboardComponent[]>('components', { default: [] });

const emit = defineEmits<{
  editComponent: [component: DashboardComponent]
}>();

onMounted(() => {
  const dashboardGridElement = dashboardGridRef.value;
  Assert.definied(dashboardGridElement);
  gridStack = GridStack.init({
    alwaysShowResizeHandle: true,
  }, dashboardGridElement);
  gridStack.on('change', detectChanges);
});

function detectChanges() {
  if (!gridStack) return;
  const items = gridStack.getGridItems();

  for (const item of items) {
    const node = item.gridstackNode;
    if (!node) continue;
    const component = components.value.find(c => c.id === node.id);
    if (!component) continue;
    const x = node.x;
    if (x === undefined) continue;
    const y = node.y;
    if (y === undefined) continue;
    const width = node.w;
    if (width === undefined) continue;
    const height = node.h;
    if (height === undefined) continue;
    component.x = x;
    component.y = y;
    component.width = width;
    component.height = height;
  }
}

function makeWidget(element: HTMLElement) {
  if (!gridStack) return;
  gridStack.makeWidget(element);
}

function deleteComponent(component: DashboardComponent, element: HTMLElement) {
  const index = components.value.findIndex(c => c.id === component.id);
  if (index < 0) return;
  gridStack?.removeWidget(element);
  components.value.splice(index, 1);
}

export interface DashboardComponentPosition {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

</script>

<template>
  <div class="dashboard-component-grid" ref="dashboard-grid">
    <DashboardComponentItem v-for="component in components" v-bind:key="component.id" v-bind:component="component"
      :make-widget="makeWidget" :delete-widget="deleteComponent" @edit="(c) => emit('editComponent', c)">
    </DashboardComponentItem>
  </div>
</template>

<style>
.dashboard-component-grid {
  overflow-y: hidden;
}
</style>
